// src/App.tsx — Router principal POW-02
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import LoginPage from '@/pages/LoginPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import AdminLayout from '@/components/layout/AdminLayout'
import DashboardPage from '@/pages/DashboardPage'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// Lazy imports de páginas futuras
import { lazy, Suspense } from 'react'
const ClientesPage    = lazy(() => import('@/pages/ClientesPage'))
const PagosPage       = lazy(() => import('@/pages/PagosPage'))
const PlanesPage      = lazy(() => import('@/pages/PlanesPage'))
const CuponesPage     = lazy(() => import('@/pages/CuponesPage'))
const CostoIaPage     = lazy(() => import('@/pages/CostoIaPage'))
const ProcesosPeriodoPage = lazy(() => import('@/pages/ProcesosPeriodoPage'))
const FacturacionPage = lazy(() => import('@/pages/FacturacionPage'))
const UsuariosPage    = lazy(() => import('@/pages/UsuariosPage'))
const EmpresaEmisoraPage = lazy(() => import('@/pages/EmpresaEmisoraPage'))
const RolesPage       = lazy(() => import('@/pages/RolesPage'))
const ReportesPage    = lazy(() => import('@/pages/ReportesPage'))
const ReporteDetallePage = lazy(() => import('@/pages/reportes/ReporteDetallePage'))
const InvitadosPage   = lazy(() => import('@/pages/InvitadosPage'))
const NotFoundPage    = lazy(() => import('@/pages/NotFoundPage'))

// Mantenimiento
const FamiliaServiciosPage = lazy(() => import('@/pages/mantenimiento/FamiliaServiciosPage'))
const ServiciosPage = lazy(() => import('@/pages/mantenimiento/ServiciosPage'))
const UnidadMedidaPage = lazy(() => import('@/pages/mantenimiento/UnidadMedidaPage'))
const UbigeoPage = lazy(() => import('@/pages/mantenimiento/UbigeoPage'))
const FormaPagoPage = lazy(() => import('@/pages/mantenimiento/FormaPagoPage'))
const MedioPagoPage = lazy(() => import('@/pages/mantenimiento/MedioPagoPage'))
const SeriesPage = lazy(() => import('@/pages/mantenimiento/SeriesPage'))
const MonedasPage = lazy(() => import('@/pages/mantenimiento/MonedasPage'))
const DocumentoVentaPage = lazy(() => import('@/pages/mantenimiento/DocumentoVentaPage'))
const DocumentoIdentidadPage = lazy(() => import('@/pages/mantenimiento/DocumentoIdentidadPage'))
const PaisesPage = lazy(() => import('@/pages/mantenimiento/PaisesPage'))
const ImpuestosPage = lazy(() => import('@/pages/mantenimiento/ImpuestosPage'))
const SectoresPage = lazy(() => import('@/pages/mantenimiento/SectoresPage'))
const TipoCambioPage = lazy(() => import('@/pages/TipoCambioPage'))

import { useInactivityTimeout } from '@/hooks/useInactivityTimeout'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { step, logout } = useAuthStore()
  useInactivityTimeout()
  const hasToken = Boolean(localStorage.getItem('pow_access_token'))
  if (step !== 'authenticated' || !hasToken) {
    if (step === 'authenticated' || hasToken) {
      logout()
    }
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

function RequireSuperadmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (!user?.es_superadmin) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
           style={{ borderColor: 'var(--color-miva-blue)', borderTopColor: 'transparent' }} />
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          style: {
            background: 'var(--color-surface-700)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
          },
        }} />

        <Routes>
          {/* Auth */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/auth/nueva-contrasena" element={<ResetPasswordPage />} />

          {/* Admin panel — requiere autenticación */}
          <Route element={<RequireAuth><AdminLayout /></RequireAuth>}>
            <Route path="dashboard"   element={<DashboardPage />} />
            <Route path="clientes"    element={<Suspense fallback={<PageLoader />}><ClientesPage /></Suspense>} />
            <Route path="pagos"       element={<Suspense fallback={<PageLoader />}><PagosPage /></Suspense>} />
            <Route path="planes"      element={<Suspense fallback={<PageLoader />}><PlanesPage /></Suspense>} />
            <Route path="cupones"     element={<Suspense fallback={<PageLoader />}><CuponesPage /></Suspense>} />
            <Route path="costo-ia"    element={<Suspense fallback={<PageLoader />}><CostoIaPage /></Suspense>} />
            <Route path="procesos-periodo" element={<Suspense fallback={<PageLoader />}><ProcesosPeriodoPage /></Suspense>} />
            <Route path="facturacion" element={<Suspense fallback={<PageLoader />}><FacturacionPage /></Suspense>} />
            <Route path="usuarios"    element={<RequireSuperadmin><Suspense fallback={<PageLoader />}><UsuariosPage /></Suspense></RequireSuperadmin>} />
            <Route path="empresa-emisora" element={<Suspense fallback={<PageLoader />}><EmpresaEmisoraPage /></Suspense>} />
            <Route path="roles"       element={<Suspense fallback={<PageLoader />}><RolesPage /></Suspense>} />
            <Route path="reportes"    element={<Suspense fallback={<PageLoader />}><ReportesPage /></Suspense>} />
            <Route path="reportes/:clave" element={<Suspense fallback={<PageLoader />}><ReporteDetallePage /></Suspense>} />
            <Route path="invitados"   element={<Suspense fallback={<PageLoader />}><InvitadosPage /></Suspense>} />
            
            <Route path="mantenimiento">
              <Route path="familia-servicios" element={<Suspense fallback={<PageLoader />}><FamiliaServiciosPage /></Suspense>} />
              <Route path="servicios" element={<Suspense fallback={<PageLoader />}><ServiciosPage /></Suspense>} />
              <Route path="unidad-medida" element={<Suspense fallback={<PageLoader />}><UnidadMedidaPage /></Suspense>} />
              <Route path="ubigeo" element={<Suspense fallback={<PageLoader />}><UbigeoPage /></Suspense>} />
              <Route path="forma-pago" element={<Suspense fallback={<PageLoader />}><FormaPagoPage /></Suspense>} />
              <Route path="medio-pago" element={<Suspense fallback={<PageLoader />}><MedioPagoPage /></Suspense>} />
              <Route path="series" element={<Suspense fallback={<PageLoader />}><SeriesPage /></Suspense>} />
              <Route path="monedas" element={<Suspense fallback={<PageLoader />}><MonedasPage /></Suspense>} />
              <Route path="documento-venta" element={<Suspense fallback={<PageLoader />}><DocumentoVentaPage /></Suspense>} />
              <Route path="documento-identidad" element={<Suspense fallback={<PageLoader />}><DocumentoIdentidadPage /></Suspense>} />
                <Route path="paises" element={<Suspense fallback={<PageLoader />}><PaisesPage /></Suspense>} />
                <Route path="impuestos" element={<Suspense fallback={<PageLoader />}><ImpuestosPage /></Suspense>} />
                <Route path="sectores" element={<Suspense fallback={<PageLoader />}><SectoresPage /></Suspense>} />
              <Route path="tipo-cambio" element={<Suspense fallback={<PageLoader />}><TipoCambioPage /></Suspense>} />
            </Route>
          </Route>

          <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFoundPage /></Suspense>} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
